<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const apiKey = ref('');
const apiSecret = ref('');
const inboxName = ref('');
const phoneNumber = ref('');

const rules = {
  inboxName: { required },
  phoneNumber: { required },
  apiKey: { required },
  apiSecret: { required },
};

const v$ = useVuelidate(rules, { inboxName, phoneNumber, apiKey, apiSecret });

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const vonageChannel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: {
        type: 'vonage',
        phone_number: phoneNumber.value,
        provider_config: {
          api_key: apiKey.value,
          api_secret: apiSecret.value,
          from: phoneNumber.value,
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: vonageChannel.id,
      },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.INBOX_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">{{
          $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.INBOX_NAME.ERROR')
        }}</span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="
            $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.PHONE_NUMBER.PLACEHOLDER')
          "
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">{{
          $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.PHONE_NUMBER.ERROR')
        }}</span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiKey.$error }">
        {{ $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_KEY.LABEL') }}
        <input
          v-model="apiKey"
          type="text"
          :placeholder="
            $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_KEY.PLACEHOLDER')
          "
          @blur="v$.apiKey.$touch"
        />
        <span v-if="v$.apiKey.$error" class="message">{{
          $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_KEY.ERROR')
        }}</span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiSecret.$error }">
        {{ $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_SECRET.LABEL') }}
        <input
          v-model="apiSecret"
          type="text"
          :placeholder="
            $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_SECRET.PLACEHOLDER')
          "
          @blur="v$.apiSecret.$touch"
        />
        <span v-if="v$.apiSecret.$error" class="message">{{
          $t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.API_SECRET.ERROR')
        }}</span>
      </label>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT_VONAGE.ADD.SMS.VONAGE.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
